import DOMPurify, { type Config as DOMPurifyConfig } from 'dompurify';
import { marked, type Token } from 'marked';

import { EXTERNAL_LINK_CLASS, MARKDOWN_IMAGE_CLASS } from './markdown-class-names';

// Languages the deferred highlighter (deferredCodeHighlight.ts) can syntax-
// highlight post-paint. renderer.code tags only these with `language-X` so the
// lazy pass knows what to upgrade; everything else renders as plain
// <pre><code>. Kept in sync with the registrations in deferredCodeHighlight.ts.
const SUPPORTED_CODE_LANGS = new Set([
  'bash',
  'c',
  'cpp',
  'css',
  'go',
  'html',
  'java',
  'javascript',
  'js',
  'json',
  'python',
  'ruby',
  'rust',
  'sql',
  'ts',
  'typescript',
  'xml',
]);

marked.setOptions({ gfm: true, breaks: true });

const renderer = new marked.Renderer();

// Escape double-quotes in attribute values to prevent attribute injection before DOMPurify runs.
function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

// The class-name constants now live in a side-effect-free module (imported
// above) so the globally mounted <ImageLightbox/> can reference
// MARKDOWN_IMAGE_CLASS without pulling marked + DOMPurify into every page.
// Re-exported here to preserve this module's existing public API.
export { EXTERNAL_LINK_CLASS, MARKDOWN_IMAGE_CLASS };

// Build a plain-text representation of a heading's tokens for slugifying.
// Walking the token tree is cheaper (and produces cleaner slugs) than parsing
// to HTML and then stripping the tags back out with a regex.
function tokensToText(tokens: Token[] | undefined): string {
  if (!tokens) return '';
  let out = '';
  for (const t of tokens) {
    const tok = t as { text?: string; tokens?: Token[] };
    if (tok.tokens) out += tokensToText(tok.tokens);
    else if (typeof tok.text === 'string') out += tok.text;
  }
  return out;
}

// Slugify heading text for anchor IDs so long bot answers can be linked /
// jumped to by section. Falls back to a short hash for headings that contain
// only emoji / punctuation.
function slugifyHeading(text: string): string {
  const slug = text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 60);
  if (slug) return slug;
  let hash = 0;
  for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) | 0;
  return `h-${Math.abs(hash).toString(36)}`;
}

renderer.heading = ({ tokens, depth }) => {
  const html = renderer.parser!.parseInline(tokens);
  const id = slugifyHeading(tokensToText(tokens));
  return `<h${depth} id="${id}">${html}</h${depth}>`;
};

function isExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

renderer.link = ({ href, title, tokens }) => {
  const text = renderer.parser!.parseInline(tokens);
  const titleAttr = title ? ` title="${escapeAttr(title)}"` : '';
  const classAttr = isExternalHref(href) ? ` class="${EXTERNAL_LINK_CLASS}"` : '';
  return `<a href="${href}"${titleAttr}${classAttr} target="_blank" rel="noopener noreferrer">${text}</a>`;
};

renderer.code = ({ text, lang }) => {
  const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  // Emit a plain, escaped code block synchronously. When the language is one we
  // can highlight, tag it with `language-X` so the deferred highlighter
  // (lazy-loaded post-paint) upgrades it in place — this keeps highlight.js out
  // of the synchronous render path AND the first-paint bundle.
  const language = lang && SUPPORTED_CODE_LANGS.has(lang) ? lang : null;
  if (!language) return `<pre><code>${escaped}</code></pre>`;
  return `<pre><code class="language-${language}">${escaped}</code></pre>`;
};

renderer.image = ({ href, title, text }) => {
  const titleAttr = title ? ` title="${escapeAttr(title)}"` : '';
  return `<img src="${href}" alt="${text}"${titleAttr} class="${MARKDOWN_IMAGE_CLASS}" loading="lazy">`;
};

const SANITIZE_CONFIG: DOMPurifyConfig = {
  ALLOWED_TAGS: [
    'p',
    'ul',
    'ol',
    'li',
    'strong',
    'em',
    'code',
    'pre',
    'a',
    'br',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'blockquote',
    'hr',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'img',
    'del',
    // Underline has no markdown syntax, so the editor↔markdown bridge preserves
    // it as inline <u> (turndown `keep(['u'])`). The renderer must allow it too,
    // or underlined text is silently stripped on the next render/reopen.
    'u',
    'span',
  ],
  ALLOWED_ATTR: [
    'href',
    'target',
    'rel',
    'title',
    'start',
    'src',
    'alt',
    'width',
    'height',
    'loading',
    'class',
    // Anchor jumps inside long bot answers.
    'id',
  ],
};

// Render an ALREADY-NORMALIZED markdown string to sanitized HTML. Split out
// from `renderMarkdown` so the per-block streaming renderer can reuse it without
// re-running the normalization passes — escaping a lone `~` twice would corrupt
// it (`~` → `\~` → `\\~`).
function renderNormalizedMarkdown(normalizedText: string): string {
  const rawHtml = marked.parse(normalizedText, { renderer }) as string;
  return DOMPurify.sanitize(rawHtml, SANITIZE_CONFIG);
}

// Streaming re-runs the full block list on every token delta, but only the last
// (growing) block actually changes — completed blocks keep the same source (and
// the same definition prefix until a new definition arrives). Memoize the
// sanitized HTML per exact render input so finished blocks are NOT re-parsed and
// re-sanitized every delta; otherwise a long structured answer runs O(blocks)
// marked + DOMPurify passes per token — the tablet CPU cost this change exists
// to remove. The caller routes ONLY completed blocks here (never the unique,
// never-re-hit streaming tail), so entries track block count, not delta count;
// the bounded LRU is just a cross-answer backstop.
const BLOCK_HTML_CACHE = new Map<string, string>();
const BLOCK_HTML_CACHE_MAX = 1000;

function renderNormalizedMarkdownCached(source: string): string {
  const cached = BLOCK_HTML_CACHE.get(source);
  if (cached !== undefined) {
    // Mark as most-recently-used so blocks re-touched each delta aren't evicted.
    BLOCK_HTML_CACHE.delete(source);
    BLOCK_HTML_CACHE.set(source, cached);
    return cached;
  }
  const html = renderNormalizedMarkdown(source);
  BLOCK_HTML_CACHE.set(source, html);
  if (BLOCK_HTML_CACHE.size > BLOCK_HTML_CACHE_MAX) {
    const oldest = BLOCK_HTML_CACHE.keys().next().value;
    if (oldest !== undefined) BLOCK_HTML_CACHE.delete(oldest);
  }
  return html;
}

/**
 * Render a markdown string to sanitized HTML.
 */
export function renderMarkdown(text: string): string {
  return renderNormalizedMarkdown(escapeApproximationTildes(normalizeEscapedMarkdown(text)));
}

/**
 * One lexer pass over NORMALIZED markdown: the top-level block source strings,
 * plus the document's link reference definitions.
 *
 * marked collects `[id]: url` definitions across the WHOLE document into
 * `tokens.links` — they are not emitted as their own blocks — and reference-
 * style links/images (`[text][id]`, `![alt][id]`) resolve against them. A per-
 * block render would lose them and show literal `[text][id]`, so the caller
 * re-attaches the definitions to each block.
 */
function lexMarkdownStream(normalized: string): { blocks: string[]; defs: string } {
  let tokens: ReturnType<typeof marked.lexer>;
  try {
    tokens = marked.lexer(normalized);
  } catch {
    // The lexer shouldn't throw on partial markdown, but never drop the answer.
    return { blocks: normalized ? [normalized] : [], defs: '' };
  }
  const blocks: string[] = [];
  for (const token of tokens) {
    const raw = typeof token.raw === 'string' ? token.raw : '';
    // `space` tokens (the blank lines between blocks) render to nothing.
    if (!raw.trim()) continue;
    blocks.push(raw);
  }
  const links = tokens.links;
  const defs = links
    ? Object.keys(links)
        .map((id) => {
          const def = links[id];
          const href = def?.href;
          if (typeof href !== 'string' || !href) return '';
          // Angle-bracket the destination and keep the title so hrefs with
          // spaces survive the round-trip — a bare `[id]: a b` reparses as a
          // paragraph and the reference stays literal. marked stores the
          // unescaped href/title, so re-escape `< > \` (href) and `" \` (title).
          const escHref = href.replace(/([\\<>])/g, '\\$1');
          const title = typeof def?.title === 'string' ? def.title : '';
          const escTitle = title ? ` "${title.replace(/(["\\])/g, '\\$1')}"` : '';
          return `[${id}]: <${escHref}>${escTitle}`;
        })
        .filter(Boolean)
        .join('\n')
    : '';
  return { blocks: blocks.length > 0 ? blocks : normalized ? [normalized] : [], defs };
}

/**
 * Split a markdown string into its top-level block SOURCE strings (no rendering).
 *
 * The streaming answer view uses this to render a reply block-by-block. An answer
 * arrives one delta at a time; re-rendering the WHOLE answer as a single
 * `dangerouslySetInnerHTML` each delta replaces every child node, so finished
 * headings and images are torn down and rebuilt ~60×/s — which reads as flicker
 * (worst on tablets, where repainting the full subtree every frame can't keep
 * up). Rendering each block as its own memoized node lets only the last
 * (still-streaming) block re-render while completed blocks keep their DOM.
 *
 * The split is append-only: once a block is no longer the last one its source is
 * frozen, so its rendered HTML — and therefore its DOM — stays byte-identical as
 * the stream grows.
 */
export function splitMarkdownBlocks(text: string): string[] {
  if (!text) return [];
  return lexMarkdownStream(escapeApproximationTildes(normalizeEscapedMarkdown(text))).blocks;
}

/**
 * The link reference definitions (`[id]: url`) present in `text`, one per line,
 * or '' when there are none. Re-attached to each streamed block so reference-
 * style links/images keep resolving while the answer streams in.
 */
export function markdownReferenceDefinitions(text: string): string {
  if (!text) return '';
  return lexMarkdownStream(escapeApproximationTildes(normalizeEscapedMarkdown(text))).defs;
}

/**
 * Render a markdown string as an array of sanitized HTML chunks, one per
 * top-level block. The concatenation is equivalent to `renderMarkdown(text)` for
 * the independent blocks LLM answers produce (headings, paragraphs, lists,
 * tables, code, blockquotes). See `splitMarkdownBlocks` for why streaming needs
 * this.
 */
export function renderMarkdownBlocks(text: string): string[] {
  if (!text) return [];
  const { blocks, defs } = lexMarkdownStream(escapeApproximationTildes(normalizeEscapedMarkdown(text)));
  // Re-attach reference definitions so `[text][id]` resolves mid-stream. They
  // render to nothing and leave non-reference blocks byte-identical (so finished
  // plain blocks stay memoized). PREPEND, not append — a trailing unclosed code
  // fence in the streaming tail would otherwise swallow them as code.
  const prefix = defs ? `${defs}\n\n` : '';
  const lastIndex = blocks.length - 1;
  return blocks.map((block, index) => {
    const source = prefix ? prefix + block : block;
    // Only completed blocks (every block but the last) keep a stable source that
    // is re-rendered on later deltas, so only they benefit from — and belong in —
    // the cache. The last block is the streaming tail: its source is unique every
    // delta and never hit again, so caching it would just fill the LRU with
    // one-off states (a long single-block answer would otherwise retain ~1000
    // growing copies — tens of MB on the very mobile path this optimizes). Render
    // the tail directly.
    return index === lastIndex ? renderNormalizedMarkdown(source) : renderNormalizedMarkdownCached(source);
  });
}

/**
 * Wrap each `<table>` in a horizontally-scrollable container so wide tables
 * (5-6 columns) scroll instead of cramming, while narrow tables still fill the
 * width. Apply to the SANITIZED display HTML only — the wrapper is a fixed,
 * trusted string added post-sanitize. Never run this on HTML that is fed into
 * an editor (TipTap parses the markup and the wrapper div corrupts the table
 * node), nor on the clipboard copy path.
 *
 * Also collapses columns that repeat the same value on every body row (common
 * when one survey check-in fans out into several item rows) into a caption
 * above the table, so shared reporter/location/source aren't duplicated.
 */
// Expand affordance injected into each table figure (a fixed, trusted string
// added post-sanitize). `<MarkdownTableLightbox/>` (brainwave) delegates clicks
// on `.md-table-expand` to open the table full-screen.
const TABLE_EXPAND_BUTTON =
  '<button type="button" class="md-table-expand" aria-label="Open table in full screen" title="Open table">' +
  '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline>' +
  '<line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></svg></button>';

const OUTLINE_TAG_RE = /<[^>]+>/g;

const decodeBasicEntities = (s: string): string =>
  s
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

const stripToText = (html: string): string =>
  decodeBasicEntities(html.replace(OUTLINE_TAG_RE, '')).replace(/\s+/g, ' ').trim();

function extractTableCells(rowInner: string): string[] {
  const cells: string[] = [];
  const re = /<(t[hd])\b[^>]*>([\s\S]*?)<\/\1>/gi;
  let match: RegExpExecArray | null;
  while ((match = re.exec(rowInner))) {
    cells.push(match[0]);
  }
  return cells;
}

function extractTableRows(sectionHtml: string): string[] {
  const rows: string[] = [];
  const re = /<tr\b[^>]*>([\s\S]*?)<\/tr>/gi;
  let match: RegExpExecArray | null;
  while ((match = re.exec(sectionHtml))) {
    rows.push(match[1]);
  }
  return rows;
}

function cellPlainText(cellHtml: string): string {
  const inner = cellHtml.replace(/^<t[hd]\b[^>]*>/i, '').replace(/<\/t[hd]>$/i, '');
  return stripToText(inner);
}

/**
 * Lift columns that are identical across every body row into a caption, and
 * keep only the varying columns in the table. No-op when there aren't at least
 * two body rows, or when every / no columns would remain.
 */
export function collapseConstantMarkdownTableColumns(html: string): string {
  if (!html || html.indexOf('<table') === -1) return html;
  return html.replace(/<table\b[^>]*>[\s\S]*?<\/table>/gi, (tableHtml) => {
    const theadMatch = /<thead\b[^>]*>([\s\S]*?)<\/thead>/i.exec(tableHtml);
    const tbodyMatch = /<tbody\b[^>]*>([\s\S]*?)<\/tbody>/i.exec(tableHtml);
    if (!theadMatch || !tbodyMatch) return tableHtml;

    const headerRows = extractTableRows(theadMatch[1]);
    const bodyRows = extractTableRows(tbodyMatch[1]);
    if (headerRows.length !== 1 || bodyRows.length < 2) return tableHtml;

    const headerCells = extractTableCells(headerRows[0]);
    const bodyCellRows = bodyRows.map(extractTableCells);
    const columnCount = headerCells.length;
    if (columnCount < 2 || bodyCellRows.some((row) => row.length !== columnCount)) {
      return tableHtml;
    }

    const constantIndexes: number[] = [];
    const varyingIndexes: number[] = [];
    for (let col = 0; col < columnCount; col += 1) {
      const first = cellPlainText(bodyCellRows[0][col]);
      const isConstant = first.length > 0 && bodyCellRows.every((row) => cellPlainText(row[col]) === first);
      if (isConstant) constantIndexes.push(col);
      else varyingIndexes.push(col);
    }

    if (constantIndexes.length === 0 || varyingIndexes.length === 0) {
      return tableHtml;
    }

    const sharedBits = constantIndexes.map((col) => {
      const label = cellPlainText(headerCells[col]) || `Column ${col + 1}`;
      const value = cellPlainText(bodyCellRows[0][col]);
      return `${label}: ${value}`;
    });
    // Values came from already-sanitized HTML; re-escape &, <, > for the caption.
    const safeCaption = `<p class="md-table-shared">${sharedBits
      .map((bit) => bit.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'))
      .join(' · ')}</p>`;

    const nextHeader = `<tr>${varyingIndexes.map((col) => headerCells[col]).join('')}</tr>`;
    const nextBody = bodyCellRows
      .map((row) => `<tr>${varyingIndexes.map((col) => row[col]).join('')}</tr>`)
      .join('');

    const rebuilt = tableHtml
      .replace(theadMatch[0], `<thead>${nextHeader}</thead>`)
      .replace(tbodyMatch[0], `<tbody>${nextBody}</tbody>`);
    return `${safeCaption}${rebuilt}`;
  });
}

export function wrapMarkdownTables(html: string): string {
  if (!html || html.indexOf('<table') === -1) return html;
  const collapsed = collapseConstantMarkdownTableColumns(html);
  // `.md-table-figure` (positioned) holds the floating expand control next to the
  // `.md-table-wrap` scroll container, so the control stays put while the table
  // scrolls. Shared-column captions (from collapse) stay inside the figure.
  return collapsed
    .replace(/(?:<p class="md-table-shared">[\s\S]*?<\/p>\s*)?<table(?=[\s>])/g, (match) => {
      const caption = match.endsWith('<table') ? match.slice(0, -'<table'.length) : '';
      return `<div class="md-table-figure">${TABLE_EXPAND_BUTTON}${caption}<div class="md-table-wrap"><table`;
    })
    .replace(/<\/table>/g, '</table></div></div>');
}

export type MarkdownOutlineEntry = { id: string; text: string; level: number };

const OUTLINE_HEADING_RE = /<h([1-6])\b([^>]*)>([\s\S]*?)<\/h\1>/gi;
const OUTLINE_ID_RE = /\bid="([^"]*)"/i;

/**
 * Pull a lightweight outline out of rendered-markdown HTML *without* a DOM, so
 * it's safe on the server and cheap to memoize. Returns every heading that
 * carries a slug `id` (renderMarkdown adds these), the first paragraph as a
 * short intro, and whether the body contains a table — enough to preview a long
 * response as its section list (one line per heading) instead of clipped prose.
 */
export function extractMarkdownOutline(html: string): {
  entries: MarkdownOutlineEntry[];
  intro: string;
  hasTable: boolean;
} {
  const entries: MarkdownOutlineEntry[] = [];
  if (html) {
    OUTLINE_HEADING_RE.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = OUTLINE_HEADING_RE.exec(html))) {
      const idMatch = OUTLINE_ID_RE.exec(match[2]);
      const text = stripToText(match[3]);
      if (idMatch?.[1] && text) entries.push({ id: idMatch[1], text, level: Number(match[1]) });
    }
  }
  const introMatch = html ? /<p\b[^>]*>([\s\S]*?)<\/p>/i.exec(html) : null;
  return {
    entries,
    intro: introMatch ? stripToText(introMatch[1]) : '',
    hasTable: !!html && html.indexOf('<table') !== -1,
  };
}

// Collapse inline whitespace to a single space for clean plain-text output,
// preserving <br> line breaks (otherwise adjacent lines run together).
function inlineText(node: Node): string {
  if (node.nodeType === Node.ELEMENT_NODE) {
    const clone = node.cloneNode(true) as HTMLElement;
    clone.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
    return (clone.textContent || '')
      .replace(/[ \t]+/g, ' ')
      .replace(/ *\n */g, '\n')
      .trim();
  }
  return (node.textContent || '').replace(/\s+/g, ' ').trim();
}

// Render an HTML <table> as aligned, padded columns so a pasted table stays
// readable in plain-text targets instead of collapsing into one run-on line.
function tableToText(table: HTMLTableElement): string {
  const rows: string[][] = [];
  table.querySelectorAll('tr').forEach((tr) => {
    const cells = Array.from(tr.querySelectorAll('th, td')).map((c) => inlineText(c));
    if (cells.length) rows.push(cells);
  });
  if (!rows.length) return '';
  const colCount = Math.max(...rows.map((r) => r.length));
  const widths = new Array<number>(colCount).fill(0);
  rows.forEach((r) => r.forEach((c, i) => (widths[i] = Math.max(widths[i], c.length))));
  const fmtRow = (r: string[]) =>
    widths
      .map((w, i) => (r[i] ?? '').padEnd(w))
      .join('  ')
      .trimEnd();
  const lines = [
    fmtRow(rows[0]),
    widths
      .map((w) => '-'.repeat(w))
      .join('  ')
      .trimEnd(),
  ];
  for (let i = 1; i < rows.length; i++) lines.push(fmtRow(rows[i]));
  return lines.join('\n');
}

// Walk block-level children, converting each to readable plain text.
function blocksToText(root: HTMLElement): string {
  const parts: string[] = [];
  root.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const t = inlineText(node);
      if (t) parts.push(t);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    switch (el.tagName.toLowerCase()) {
      case 'table':
        parts.push(tableToText(el as HTMLTableElement));
        break;
      case 'ul':
      case 'ol': {
        const ordered = el.tagName.toLowerCase() === 'ol';
        const items: string[] = [];
        el.querySelectorAll(':scope > li').forEach((li, i) => {
          items.push((ordered ? `${i + 1}. ` : '• ') + inlineText(li));
        });
        parts.push(items.join('\n'));
        break;
      }
      case 'pre':
        parts.push((el.textContent || '').replace(/\n+$/, ''));
        break;
      case 'hr':
        parts.push('———');
        break;
      default:
        parts.push(inlineText(el));
    }
  });
  return parts.filter(Boolean).join('\n\n');
}

/**
 * Convert a markdown string to readable plain text for the clipboard's
 * `text/plain` flavor: tables become aligned columns, lists get bullets, and
 * markdown syntax (`**`, `|`, `#`) is dropped — so users pasting into a
 * plain-text field see clean text instead of raw markdown.
 */
export function renderMarkdownToPlainText(text: string): string {
  const html = renderMarkdown(text);
  if (typeof DOMParser === 'undefined') {
    // SSR / no-DOM fallback: strip tags crudely.
    return html
      .replace(/<[^>]+>/g, '')
      .replace(/\*\*+/g, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (
    blocksToText(doc.body)
      // Upstream summaries sometimes arrive PRE-BROKEN mid-token ("Purpose:**
      // Keep…" — the leading pair was sliced off before markdown ever parsed),
      // so orphan emphasis runs survive to plain text. Legit plain-text "**"
      // is vanishingly rare; drop the tokens (qa170).
      .replace(/\*\*+/g, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  );
}

/**
 * Some clipboard/API payloads provide markdown as a JSON-escaped string (`\\n`, `\\t`).
 * Convert those escaped whitespace sequences back to characters so markdown can render.
 */
function normalizeEscapedMarkdown(text: string): string {
  if (!text) {
    return text;
  }

  const hasEscapedWhitespace = /\\[nrt]/.test(text);
  const hasLiteralLineBreak = /\n|\r/.test(text);

  if (!hasEscapedWhitespace || hasLiteralLineBreak) {
    return text;
  }

  return text.replace(/\\n/g, '\n').replace(/\\r/g, '\r').replace(/\\t/g, '\t');
}

/**
 * marked v15's GFM strikethrough tokenizer accepts ONE OR TWO tildes
 * (`~{1,2}`), so an LLM-emitted "approximately" prefix like `~50 GW/year`
 * pairs with the next lone `~` and silently turns the span between them
 * into <del>. Escape every lone `~` (not part of `~~`) so it passes through
 * as literal text — `~~strikethrough~~` keeps working.
 */
function escapeApproximationTildes(text: string): string {
  if (!text || text.indexOf('~') === -1) return text;
  return text.replace(/(?<!~)~(?!~)/g, '\\~');
}
